import { useEffect, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { useIsMobile } from '@hooks/useIsMobile';
import { cn } from '@utils/index';

export interface BottomNavOption<T extends string = string> {
  id: T;
  title: string;
  icon?: ReactNode;
}

interface BottomNavigationProps<T extends string> {
  options: BottomNavOption<T>[];
  selectedItem: T;
  onChange: (id: T) => void;
  /** On phones render the bottom navigation (icon above label) instead of tabs. */
  bottomView?: boolean;
  /** On phones let the tab strip scroll instead of squeezing every tab in. */
  scrollable?: boolean;
  customRootStyle?: CSSProperties;
}

/** Legacy mobile breakpoint (src/components/hooks/useWindowDimensions.js MOBILE_SIZE). */
export const LEGACY_MOBILE_SIZE = 740;
/** Height of the bar: 48px tabs on desktop, 56px bottom navigation on phones. */
export const BOTTOM_NAV_HEIGHT = 48;
export const BOTTOM_NAV_HEIGHT_MOBILE = 56;

const ROOT_STYLE: CSSProperties = {
  position: 'absolute',
  bottom: 0,
  left: 0,
  zIndex: 1000,
  width: '100%',
  backgroundColor: '#2961FD',
};

/**
 * The legacy page-section switcher (src/components/fragment/BottomNavigation):
 * a #2961FD bar pinned to the bottom of the page with full-width tabs (icon +
 * title, selected tab darkened). Pages render it as the last child of a
 * `position: relative` full-height column and pad their content for it.
 * eti-ui has no tabs, so the bar is a plain tablist.
 */
const BottomNavigation = <T extends string>({
  options,
  selectedItem,
  onChange,
  bottomView,
  scrollable,
  customRootStyle,
}: BottomNavigationProps<T>) => {
  const isMobile = useIsMobile(LEGACY_MOBILE_SIZE);
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    const idx = options.findIndex((o) => o.id === selectedItem);
    if (idx >= 0 && idx !== selectedIndex) setSelectedIndex(idx);
  }, [selectedItem, options, selectedIndex]);

  const handleChange = (idx: number) => {
    setSelectedIndex(idx);
    onChange(options[idx].id);
  };

  const phoneBar = isMobile && bottomView;
  const scrolls = isMobile && scrollable && !phoneBar;

  return (
    <div style={customRootStyle ?? ROOT_STYLE} className='palmetto-print-hide'>
      <div
        role='tablist'
        className={cn(
          'rep:flex rep:w-full',
          scrolls ? 'rep:overflow-x-auto' : 'rep:overflow-hidden',
        )}
        style={{ height: phoneBar ? BOTTOM_NAV_HEIGHT_MOBILE : BOTTOM_NAV_HEIGHT }}>
        {options.map((item, idx) => {
          const selected = idx === selectedIndex;
          return (
            <button
              key={item.id || idx}
              type='button'
              role='tab'
              aria-selected={selected}
              onClick={() => handleChange(idx)}
              className={cn(
                'rep:flex rep:h-full rep:cursor-pointer rep:items-center rep:justify-center rep:font-medium rep:whitespace-nowrap rep:transition-colors',
                scrolls ? 'rep:flex-none rep:px-4' : 'rep:min-w-0 rep:flex-1 rep:px-3',
                phoneBar
                  ? 'rep:flex-col rep:gap-0.5 rep:text-xs'
                  : cn('rep:gap-2', isMobile ? 'rep:text-xs' : 'rep:text-base'),
                selected
                  ? phoneBar
                    ? 'rep:bg-[rgba(41,97,253,0.85)] rep:text-white'
                    : 'rep:bg-black/12 rep:text-white'
                  : phoneBar
                    ? 'rep:text-white/60'
                    : 'rep:text-white/70',
              )}>
              {item.icon}
              <span className={scrolls ? undefined : 'rep:truncate'}>{item.title}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default BottomNavigation;
